/**
 * Normalize Home Assistant's service target metadata into the small contract
 * needed by the editor and export pipeline. The service catalog remains the
 * source of truth; this helper deliberately does not contain service names.
 */
export function targetContractFromServiceOption(option) {
  if (!option) return null
  const target = option.target && typeof option.target === 'object' && !Array.isArray(option.target)
    ? option.target
    : {}
  const contract = {
    known: true,
    entity: hasTargetKind(target, 'entity'),
    device: hasTargetKind(target, 'device'),
    area: hasTargetKind(target, 'area'),
  }
  contract.supportsAny = contract.entity || contract.device || contract.area
  return contract
}

export function targetContractFromAction(action) {
  const saved = action?.editorMetadata?.serviceTarget
  if (saved?.known === true) {
    const contract = {
      known: true,
      entity: saved.entity === true,
      device: saved.device === true,
      area: saved.area === true,
    }
    contract.supportsAny = contract.entity || contract.device || contract.area
    return contract
  }

  // Legacy provider/service pairs predate the HA service catalog. Preserve
  // their established Primary Entity behavior, except for provider-specific
  // actions that already had no entity-target semantics.
  if (action?.provider) {
    const providerTarget = !['openhasp', 'esphome'].includes(action.provider)
    return {
      known: false,
      legacy: true,
      entity: providerTarget,
      device: false,
      area: false,
      supportsAny: providerTarget,
    }
  }

  // A full domain.service identifier without catalog metadata is unknown.
  // Preserve explicit targets, but never invent an inherited one.
  return {
    known: false,
    legacy: false,
    entity: false,
    device: false,
    area: false,
    supportsAny: false,
  }
}

export function serviceTargetContract(action, serviceOptions = []) {
  const serviceId = action?.provider && action?.service
    ? `${action.provider}.${action.service}`
    : action?.service
  const option = serviceOptions.find(item => item?.value === serviceId)
  return targetContractFromServiceOption(option) ?? targetContractFromAction(action)
}

/** Persist only the target contract, not the full HA catalog, in editor metadata. */
export function rememberServiceTargetContract(action, option) {
  const contract = targetContractFromServiceOption(option)
  if (!action || !contract) return action
  const current = action.editorMetadata?.serviceTarget
  if (current && JSON.stringify(current) === JSON.stringify(contract)) return action
  action.editorMetadata = {
    ...(action.editorMetadata ?? {}),
    serviceTarget: contract,
  }
  return action
}

export function clearServiceTargetContract(action) {
  if (!action?.editorMetadata?.serviceTarget) return action
  const { serviceTarget: ignored, ...rest } = action.editorMetadata
  action.editorMetadata = Object.keys(rest).length > 0 ? rest : null
  return action
}

function hasTargetKind(target, kind) {
  return target[kind] !== undefined || target[`${kind}_id`] !== undefined
}
